'use client'

import { JSX, PropsWithChildren, useTransition } from 'react'
import { Box, Flex, RatingGroup, Text } from '@chakra-ui/react'
import { Region } from '@leid/api-locations'
import { ProductCategory } from '@leid/api-products'

import { CategoriesInfo } from 'app/(pages)/browse/[...categoryName]/helpers'
import { BrowsePageSearchParams } from 'app/(pages)/browse/models'
import Container from 'app/components/shared/Container'
import Pagination from 'app/components/shared/Pagination'
import { CustomerInfo } from 'app/utils/authentication'
import { SortingInfo } from 'appTypes/mapper'
import { Metadata } from 'appTypes/shared'
import CategoriesBreadcrumbs from 'components/partials/browse/CategoriesBreadcrumbs'
import { Loader } from 'components/ui/Loader'
import { usePathname, useRouter } from 'next/navigation'
import { revalidatePathOnDemand } from 'app/serverActions/routes'
import SortButton from './SortButton'
import CategoryDropdown from './CategoryDropdown'
import { SearchForProducts } from 'components/form/SearchForProducts'
import { useTranslation } from 'react-i18next'
import { Category } from 'app/utils/helpers/helpers'
import { convertSerachParamsToURLSearchParams } from 'app/utils/urls'
// import { DateTimePickerChangeHandler, DateTimePickerHandle } from 'components/form/DateTimePicker/interfaces'
// import { buildDate } from 'utils/dates'
// import { DateValues, parseBrowsePageSearchParamsDates } from 'app/utils/helpers/searchQuery'

type Props = PropsWithChildren & {
  isBundles?: boolean
  categories: Category[] | ProductCategory[]
  metadata: Metadata | undefined
  query: {
    [key: string]: string | string[] | undefined
  }
  sortingInfo: SortingInfo | undefined
  customerInfo: CustomerInfo
  searchParams?: BrowsePageSearchParams
  categoriesInfo?: CategoriesInfo
  breadcrumbs?: {
    url: string
    label: string
  }[]
  regions: Region[]
  isProd: boolean
}

const BrowseNavigationContext = ({
  children,
  isBundles,
  categories,
  metadata,
  query,
  sortingInfo,
  customerInfo,
  regions,
  searchParams,
  categoriesInfo,
  breadcrumbs,
  isProd,
}: Props): JSX.Element => {
  const pathname = usePathname()
  const router = useRouter()

  const [isPending, startTransition] = useTransition()
  // const [values, setValues] = useState<DateValues>(parseBrowsePageSearchParamsDates(searchParams))

  // const pickupRef = useRef<DateTimePickerHandle>(null)
  // const returnRef = useRef<DateTimePickerHandle>(null)

  const onSortButtonChange = async () => {
    await revalidatePathOnDemand(pathname ?? '')
    if (searchParams) {
      startTransition(() => {
        router.push(`${pathname}?${convertSerachParamsToURLSearchParams(searchParams)?.toString()}`)
      })
    } else {
      startTransition(() => {
        router.push(`${pathname}`)
      })
    }
  }

  // const onPickupDateChange: DateTimePickerChangeHandler = ({ day, month, year, time }) => {
  //   const updatedDate = new Date(year, month, day, Number(time))
  //   if (values.startDate?.getTime() === updatedDate.getTime() && values.pickupAt === time) {
  //     return
  //   }

  //   setValues({
  //     startDate: updatedDate,
  //     pickupAt: time,
  //     endDate: undefined,
  //     returnAt: undefined,
  //   })

  //   pickupRef?.current?.collapse()
  // }

  // const onReturnChange: DateTimePickerChangeHandler = async ({ day, month, year, time }) => {
  //   const updatedDate = new Date(year, month, day, Number(time))
  //   if (values.endDate?.getTime() === updatedDate.getTime() && values.returnAt === time) {
  //     return
  //   }

  //   setValues((prevValues) => ({
  //     ...prevValues,
  //     endDate: updatedDate,
  //     returnAt: time,
  //   }))

  //   returnRef?.current?.collapse()

  //   if (values.startDate && values.pickupAt) {
  //     await revalidatePathOnDemand(pathname ?? '')

  //     const newSearchParams: BrowsePageSearchParams = searchParams ?? ({} as BrowsePageSearchParams)
  //     newSearchParams.rentedFrom = buildDate(values.startDate, values.pickupAt).toISOString()
  //     newSearchParams.rentedTo = updatedDate.toISOString()

  //     startTransition(() => {
  //       router.push(`${pathname}?${convertSerachParamsToURLSearchParams(newSearchParams)?.toString()}`)
  //     })
  //   }
  // }

  const { t } = useTranslation('common')

  return (
    <>
      <SortButton
        showOnStartup
        sortingInfo={sortingInfo}
        customerInfo={customerInfo}
        regions={regions}
        isProd={isProd}
        queryParams={query}
        onCoordinatesSelect={onSortButtonChange}
      />
      <Container>
        <Box marginTop={{ base: '20px', md: '28px' }} marginBottom="0px">
          <Flex alignItems="start" gap={2} justifyContent="space-between" direction={{ base: 'column', md: 'row' }}>
            <Box>
              <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight="bold" textTransform="capitalize">
                {isBundles ? t('productBundles') : t('products')}
              </Text>
              <RatingGroup.Root count={5} value={4.5} size="xs" colorPalette="orange" readOnly>
                <RatingGroup.HiddenInput />
                <RatingGroup.Control />
              </RatingGroup.Root>
              <Text fontSize="sm" fontWeight="normal" color="gray.a500" marginBottom="12px">
                {t('averageRating')}
              </Text>
            </Box>

            {/* TODO: Return back when it's ready */}
            {/* <Box width="100%">
              <DatePickersWrapper id="date-time-pickers">
                <Box id="pickup-date-picker">
                  <DateTimePicker
                    title={t('pickup')}
                    ref={pickupRef}
                    isPickup={true}
                    isProlongation={false}
                    onChange={onPickupDateChange}
                    defaultCollapsed={true}
                    disabled={false}
                    defaultDate={values.startDate}
                    defaultTime={values.pickupAt}
                  />
                </Box>
                <Box id="return-date-picker">
                  <DateTimePicker
                    title={t('return')}
                    ref={returnRef}
                    isPickup={false}
                    isProlongation={false}
                    onChange={onReturnChange}
                    defaultCollapsed={true}
                    disabled={!values.startDate || !values.pickupAt}
                    defaultDate={values.endDate}
                    defaultTime={values.returnAt}
                  />
                </Box>
              </DatePickersWrapper>
            </Box> */}

            <Flex gap={2} alignItems="center">
              <CategoryDropdown
                isBundles={isBundles ?? false}
                searchCategory={searchParams?.category}
                allCategories={categories}
                queryParams={query}
                startTransition={startTransition}
              />
            </Flex>
          </Flex>
        </Box>

        <Box display={{ md: 'none' }} marginTop="20px">
          <SearchForProducts redirectTo="browse" />
        </Box>

        {/* Category info is defined */}
        {categoriesInfo && <CategoriesBreadcrumbs items={breadcrumbs ?? []} startTransition={startTransition} />}
      </Container>

      <Box minHeight="57vh">{isPending ? <Loader /> : <>{children}</>}</Box>

      {metadata?.last !== undefined && metadata?.last > 1 && (
        <Box marginBottom="32px">
          <Pagination
            queryParams={query}
            pageCount={metadata.last}
            currentPage={metadata.page}
            startTransition={startTransition}
          />
        </Box>
      )}
    </>
  )
}

export default BrowseNavigationContext
